import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Calendar, SlotInfo, Event as CEvent, dayjsLocalizer } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import PageTitle from "../../../components/common/PageTitle/PageTitle";
import CalendarEventModal from "./CalendarEventModal";
import { CalendarContainer, StyledSwitchContainer } from "./Calendar.styles";
import { CalendarEvent, CalendarServiceModule, YearlyEventVisibility } from "../../../types/serviceModuleTypes";
import { useAppDispatch } from "../../../hooks/StoreHooks";
import dayjs from "dayjs";
import { useParams } from "react-router";
import {
    createCalendarEventThunk,
    deleteCalendarEventThunk,
    fetchCalendarEventsThunk,
    fetchCalendarModuleThunk,
    updateCalendarEventThunk,
    updateCalendarModuleThunk
} from "../../../store/thunk/serviceModulesThunk";
import { Box, FormControlLabel, Radio, Stack, Typography } from "@mui/material";
import { theme } from "../../../theme";
import DefaultIcon from "../../../components/common/DefaultIcon";

const localizer = dayjsLocalizer(dayjs);

const breadcrumbs = [
    { title: "serviceModulesPage.serviceModules", link: "serviceModules" },
    { title: "serviceModulesPage.calendar", link: "serviceModules/calendar" },
];

const CalendarEventComponent = ({ event }) => {
    const e = event.resource;
    return (
        <Box display="flex" alignItems="center">
            <Box
                sx={{
                    transform: "scale(0.75)",
                    transformOrigin: "left center",
                    display: "flex",
                    alignItems: "center"
                }}
            >
                <DefaultIcon
                    iconName={e.iconName}
                    colorHex={e.colorHex}
                    iconUrl={e.iconUrl}
                />
            </Box>
            <Typography variant="body2" noWrap>
                {event.title}
            </Typography>
        </Box>
    );
};

const CalendarPage = () => {
    const { t, i18n } = useTranslation();
    const dispatch = useAppDispatch();
    const { serviceModuleId } = useParams();
    const calendarId = Number(serviceModuleId)!;

    const [isModalOpen, setModalOpen] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
    const [loadedEvents, setLoadedEvents] = useState<CalendarEvent[]>([]);
    const [dateRange, setDateRange] = useState<{ start: Date; end: Date }>({
        start: dayjs().startOf('month').toDate(),
        end: dayjs().endOf('month').toDate(),
    });
    const [calendarData, setCalendarData] = useState<CalendarServiceModule | null>(null);

    useEffect(() => {
        fetchCalendarData();
    }, [calendarId]);

    useEffect(() => {
        fetchEvents();
    }, [calendarId, dateRange]);

    const fetchCalendarData = async () => {
        const result = await dispatch(fetchCalendarModuleThunk(Number(calendarId)));
        if (result.payload) {
            setCalendarData(result.payload as CalendarServiceModule);
        }
    };

    const fetchEvents = async () => {
        const now = dayjs()
        const result = await dispatch(fetchCalendarEventsThunk({
            serviceModuleId: calendarId,
            startDate: dateRange?.start ?? now.startOf('month').toDate(),
            endDate: dateRange?.end ?? now.endOf('month').toDate(),
        }));
        setLoadedEvents(result.payload as CalendarEvent[]);
    };

    const eventsForCalendarView = useMemo<CEvent[]>(() =>
        loadedEvents.map((e) => {
            let eventTitle: string;
            switch (e.eventType) {
                case "Anniversary":
                    const anniversaryYearsVisible = calendarData?.anniversaryVisibility === YearlyEventVisibility.VisibleWithYear;
                    eventTitle = `🎉 ${e.title} ${anniversaryYearsVisible ? `(${e.years})` : ""}`
                    break;
                case "Birthday":
                    const birthdayYearsVisible = calendarData?.birthdayVisibility === YearlyEventVisibility.VisibleWithYear;
                    eventTitle = `🎂 ${e.title} ${birthdayYearsVisible ? `(${e.years})` : ""}`
                    break;
                case "Absence":
                    eventTitle = t("serviceModulesPage.calendarPage.absenceTitle", { name: e.title });
                    break;
                default:
                    eventTitle = e.title
                    break;
            }

            return {
                allDay: !e.hasTime,
                title: eventTitle.trim(),
                start: dayjs(e.startDate).utcOffset(0).toDate(),
                end: dayjs(e.endDate).utcOffset(0).toDate(),
                resource: e,
            };
        }),
        [loadedEvents, calendarData]
    );

    const handleDateRangeChanged = (range: Date[] | { start: Date; end: Date }) => {
        if (!Array.isArray(range)) {
            setDateRange(range);
        } else {
            const [start, end] = [range[0], range.length > 1 ? range[range.length - 1] : range[0]];
            setDateRange({ start, end });
        }
    };

    const handleSelectSlot = (info: SlotInfo) => {
        setSelectedEvent({
            title: "",
            description: "",
            capacity: null,
            startDate: info.start.toISOString(),
            endDate: info.start.toISOString(),
            iconName: "circle",
            colorHex: theme.palette.primary.main,
            iconUrl: null,
            hasTime: false,
            isCompanyWideAssigned: true,
            assignedGroupIds: []
        });
        setModalOpen(true);
    };

    const handleSelectEvent = (event: CEvent) => {
        if (!event.resource) return;

        const clickedEvent = event.resource as CalendarEvent;

        if (clickedEvent.eventType === "Basic") {
            setSelectedEvent(clickedEvent);
            setModalOpen(true);
        }
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setSelectedEvent(null);
    };

    const handleSaveEvent = (eventData: Partial<CalendarEvent>) => {
        if (eventData.id) {
            dispatch(updateCalendarEventThunk({
                serviceModuleId: calendarId,
                eventData: eventData,
                eventId: eventData.id
            })).then(() => fetchEvents());
        } else {
            dispatch(createCalendarEventThunk({
                serviceModuleId: calendarId,
                eventData: eventData
            })).then(() => fetchEvents());
        }
        handleCloseModal();
    };

    const handleDeleteEvent = (eventId: number) => {
        dispatch(deleteCalendarEventThunk({
            serviceModuleId: calendarId,
            eventId: eventId
        })).then(() => fetchEvents());
        handleCloseModal();
    };

    const handleCopyEvent = (eventData: CalendarEvent) => {
        const copiedEvent: CalendarEvent = {
            ...eventData,
            id: undefined,
            title: `${eventData.title} Copy`,
            startDate: dayjs(eventData.startDate).add(1, "day").toISOString(),
            endDate: dayjs(eventData.endDate).add(1, "day").toISOString(),
            sendUpdateNotification: false,
            reminderNotifications: [],
        };
        setSelectedEvent(copiedEvent);
        setModalOpen(true);
    };

    const handleChangeYearlyVisibility = async (field: keyof CalendarServiceModule, visibility: YearlyEventVisibility) => {
        if (!calendarData) return;

        const updatedData = {
            ...calendarData,
            [field]: visibility
        };

        setCalendarData(updatedData);
        await dispatch(updateCalendarModuleThunk({
            serviceModuleId: calendarId,
            data: updatedData
        }));
        fetchEvents();
    };

    const calendarEventTitleAccessor = (event: CEvent) => {
        const eventData = event.resource as CalendarEvent;
        if (!eventData) return "";

        const name = eventData.title;
        const yearsText = eventData.years ? ` (${eventData.years})` : "";

        switch (eventData.eventType) {
            case "Birthday":
                return t("serviceModulesPage.calendarPage.birthdayTooltip", { name }) + yearsText;
            case "Anniversary":
                return t("serviceModulesPage.calendarPage.anniversaryTooltip", { name }) + yearsText;
            case "Absence":
                return t("serviceModulesPage.calendarPage.absenceTitle", { name });
            case "Basic":
            default:
                return eventData.title;
        }
    };

    return <>
        <PageTitle breadcrumbs={breadcrumbs} title={t("serviceModulesPage.calendar")} />
        <CalendarContainer>
            <Calendar
                localizer={localizer}
                events={eventsForCalendarView}
                selectable
                onSelectSlot={handleSelectSlot}
                onSelectEvent={handleSelectEvent}
                components={{
                    event: CalendarEventComponent
                }}
                messages={{
                    previous: t("serviceModulesPage.calendarPage.calendarPrevious"),
                    next: t("serviceModulesPage.calendarPage.calendarNext"),
                    today: t("serviceModulesPage.calendarPage.calendarToday"),
                }}
                culture={i18n.language}
                style={{ height: "100%", flex: 1 }}
                views={{
                    month: true,
                    agenda: false,
                    day: false,
                    week: false,
                    work_week: false
                }}
                onRangeChange={handleDateRangeChanged}
                tooltipAccessor={calendarEventTitleAccessor}
            />
        </CalendarContainer>

        <StyledSwitchContainer>
            <YearlyEventVisibilityPicker
                title={t("serviceModulesPage.areBirthdaysVisible")}
                value={calendarData?.birthdayVisibility}
                onChanged={(newValue: YearlyEventVisibility) => handleChangeYearlyVisibility("birthdayVisibility", newValue)}
            />

            <YearlyEventVisibilityPicker
                title={t("serviceModulesPage.areAnniversariesVisible")}
                value={calendarData?.anniversaryVisibility}
                onChanged={(newValue: YearlyEventVisibility) => handleChangeYearlyVisibility("anniversaryVisibility", newValue)}
            />
        </StyledSwitchContainer>

        {selectedEvent && calendarData && <CalendarEventModal
            event={selectedEvent}
            serviceModule={calendarData}
            onSave={handleSaveEvent}
            onDelete={handleDeleteEvent}
            onCopy={handleCopyEvent}
            handleClose={handleCloseModal}
            open={isModalOpen}
        />}
    </>;
};

interface YearlyEventVisibilityPickerProps {
    title: string;
    value: YearlyEventVisibility | undefined;
    onChanged: (newValue: YearlyEventVisibility) => void;
}

function YearlyEventVisibilityPicker(props: YearlyEventVisibilityPickerProps) {
    const { t } = useTranslation();

    return <Stack direction="column">
        <Typography variant="subtitle2">{props.title}</Typography>
        <FormControlLabel
            label={t("serviceModulesPage.calendarPage.yearlyEventVisibilityHidden")}
            control={
                <Radio
                    checked={props.value === YearlyEventVisibility.Hidden}
                    onChange={() => props.onChanged(YearlyEventVisibility.Hidden)}
                    color="primary"
                />
            } />
        <FormControlLabel
            label={t("serviceModulesPage.calendarPage.yearlyEventVisibilityVisible")}
            control={
                <Radio
                    checked={props.value === YearlyEventVisibility.Visible}
                    onChange={() => props.onChanged(YearlyEventVisibility.Visible)}
                    color="primary"
                />
            } />
        <FormControlLabel
            label={t("serviceModulesPage.calendarPage.yearlyEventVisibilityVisibleWithYears")}
            control={
                <Radio
                    checked={props.value === YearlyEventVisibility.VisibleWithYear}
                    onChange={() => props.onChanged(YearlyEventVisibility.VisibleWithYear)}
                    color="primary"
                />
            } />
    </Stack>;
}


export default CalendarPage;
